import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline'

interface DepositDetail {
  id: string
  user_id: string
  user_name: string
  user_email: string
  amount: number
  fee: number
  real_amount: number
  bonus_amount: number
  bonus_rate: number
  payment_method: string
  payment_reference?: string
  status: string
  payment_proof?: string
  processed_at?: string
  created_at: string
}

export default function DepositDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [deposit, setDeposit] = useState<DepositDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchDepositDetail()
  }, [id])

  const fetchDepositDetail = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiClient.getDepositDetail(id!)
      if (response.success && response.data?.deposits && response.data.deposits.length > 0) {
        setDeposit(response.data.deposits[0])
      } else {
        setError(response.message || '加载充值订单失败')
      }
    } catch (err) {
      setError('加载充值订单失败')
      console.error('Failed to fetch deposit detail:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { bg: string; text: string; icon: any; label: string }> = {
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon, label: '待支付' },
      'processing': { bg: 'bg-blue-100', text: 'text-blue-800', icon: ClockIcon, label: '处理中' },
      'completed': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon, label: '已完成' },
      'failed': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircleIcon, label: '失败' },
    }
    const config = statusMap[status] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: ClockIcon, label: status }
    const Icon = config.icon
    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${config.bg} ${config.text} text-sm font-medium`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center space-y-4">
            <div className="loading-spinner w-8 h-8"></div>
            <p className="text-sm text-gray-500">加载中...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !deposit) {
    return (
      <div className="page-content">
        <div className="mb-8">
          <button
            onClick={() => navigate('/deposits')}
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            返回充值订单列表
          </button>
          <h1 className="page-title">充值订单详情</h1>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="text-center py-8">
              <p className="text-red-600">{error || '充值订单不存在'}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/deposits')}
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-4"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          返回充值订单列表
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">充值订单详情</h1>
            <p className="page-subtitle">订单ID: {deposit.id.substring(0, 8)}</p>
          </div>
          <div>{getStatusBadge(deposit.status)}</div>
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">用户信息</h2>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">用户名</label>
              <p className="text-gray-900 mt-1">{deposit.user_name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">邮箱</label>
              <p className="text-gray-900 mt-1">{deposit.user_email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">用户ID</label>
              <p className="text-gray-900 mt-1 font-mono text-sm">{deposit.user_id}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">支付信息</h2>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">支付方式</label>
              <p className="text-gray-900 mt-1">{deposit.payment_method}</p>
            </div>
            {deposit.payment_reference && (
              <div>
                <label className="text-sm font-medium text-gray-600">支付参考号</label>
                <p className="text-gray-900 mt-1 font-mono text-sm">{deposit.payment_reference}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-600">订单状态</label>
              <p className="mt-1">{getStatusBadge(deposit.status)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Amount Details */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-600">充值金额</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {formatCurrency(deposit.amount)}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-600">手续费</p>
            <p className="text-2xl font-bold text-red-600 mt-2">
              -{formatCurrency(deposit.fee)}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-600">实际到账</p>
            <p className="text-2xl font-bold text-green-600 mt-2">
              {formatCurrency(deposit.real_amount)}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-600">赠送金额</p>
            <p className="text-2xl font-bold text-blue-600 mt-2">
              {formatCurrency(deposit.bonus_amount)}
            </p>
            {deposit.bonus_rate > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                ({(deposit.bonus_rate * 100).toFixed(1)}%)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="card-title">金额汇总</h2>
        </div>
        <div className="card-body">
          <div className="space-y-3">
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <span className="text-gray-600">充值金额</span>
              <span className="font-semibold text-gray-900">{formatCurrency(deposit.amount)}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <span className="text-gray-600">手续费</span>
              <span className="font-semibold text-red-600">-{formatCurrency(deposit.fee)}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <span className="text-gray-600">实际到账（真实余额）</span>
              <span className="font-semibold text-green-600">{formatCurrency(deposit.real_amount)}</span>
            </div>
            <div className="flex justify-between items-center pt-3 bg-blue-50 px-3 py-2 rounded">
              <span className="text-gray-600 font-medium">赠送金额（赠送余额）</span>
              <span className="font-bold text-blue-600">{formatCurrency(deposit.bonus_amount)}</span>
            </div>
            <div className="flex justify-between items-center pt-3 bg-primary-50 px-3 py-2 rounded">
              <span className="text-gray-600 font-medium">用户总获得</span>
              <span className="font-bold text-primary-600">
                {formatCurrency(deposit.real_amount + deposit.bonus_amount)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Time Info */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">时间信息</h2>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-600">创建时间</label>
            <p className="text-gray-900 mt-1">{formatDate(deposit.created_at)}</p>
          </div>
          {deposit.processed_at && (
            <div>
              <label className="text-sm font-medium text-gray-600">处理时间</label>
              <p className="text-gray-900 mt-1">{formatDate(deposit.processed_at)}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
